import { useMemo, useState, useCallback } from 'react';
import type { Appointment } from '@/mocks/appointments';
import { StatusBadge } from '@/components/base/StatCard';

interface MonthlyCalendarProps {
  year: number;
  month: number; // 0-11
  appointments: Appointment[];
  onDayClick: (date: string) => void;
  onAppointmentClick: (appt: Appointment) => void;
  onAppointmentUpdate?: (appt: Appointment) => void;
}

const TURKISH_MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const DAY_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

function getStatusColor(status: Appointment['status']): string {
  switch (status) {
    case 'Geldi': return '#10B981';
    case 'Gelmedi': return '#EF4444';
    case 'İptal Etti': return '#9CA3AF';
    case 'Teyit Edildi': return '#3B82F6';
    default: return '#C9A962';
  }
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function MonthlyCalendar({ year, month, appointments, onDayClick, onAppointmentClick, onAppointmentUpdate }: MonthlyCalendarProps) {
  const [expandedDay, setExpandedDay] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropTargetDate, setDropTargetDate] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split('T')[0];

  const calendarData = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    // Monday = 0 in our grid
    let startOffset = firstDay.getDay() - 1; // 0=Sun -> we want Mon=0
    if (startOffset < 0) startOffset = 6;

    const days: { date: string; dayNum: number; isCurrentMonth: boolean }[] = [];

    // Previous month filler
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startOffset - 1; i >= 0; i--) {
      const d = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      days.push({ date: formatDate(prevYear, prevMonth, d), dayNum: d, isCurrentMonth: false });
    }

    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      days.push({ date: formatDate(year, month, d), dayNum: d, isCurrentMonth: true });
    }

    // Next month filler to complete 6 rows (42 cells)
    const remaining = 42 - days.length;
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;
    for (let d = 1; d <= remaining; d++) {
      days.push({ date: formatDate(nextYear, nextMonth, d), dayNum: d, isCurrentMonth: false });
    }

    return days;
  }, [year, month]);

  const apptsByDate = useMemo(() => {
    const map: Record<string, Appointment[]> = {};
    appointments.forEach(a => {
      if (!map[a.date]) map[a.date] = [];
      map[a.date].push(a);
    });
    return map;
  }, [appointments]);

  const monthAppts = useMemo(() => {
    return appointments.filter(a => {
      const d = new Date(a.date + 'T00:00:00');
      return d.getFullYear() === year && d.getMonth() === month;
    });
  }, [appointments, year, month]);

  const handleDragStart = useCallback((e: React.DragEvent, appt: Appointment) => {
    setDraggingId(appt.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', appt.id);
    const el = e.currentTarget as HTMLElement;
    if (el) {
      e.dataTransfer.setDragImage(el, 10, 10);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, date: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDate(date);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTargetDate(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, date: string) => {
    e.preventDefault();
    const apptId = e.dataTransfer.getData('text/plain');
    if (!apptId || !onAppointmentUpdate) {
      setDraggingId(null);
      setDropTargetDate(null);
      return;
    }

    const appt = appointments.find(a => a.id === apptId);
    if (!appt) {
      setDraggingId(null);
      setDropTargetDate(null);
      return;
    }

    const updated: Appointment = {
      ...appt,
      date,
      // Keep serviceItems[0].staff in sync for consistency
      serviceItems: appt.serviceItems?.length
        ? appt.serviceItems.map((si, i) => i === 0 ? { ...si, staff: appt.staff } : si)
        : undefined,
    };
    onAppointmentUpdate(updated);
    setDraggingId(null);
    setDropTargetDate(null);
  }, [appointments, onAppointmentUpdate]);

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDropTargetDate(null);
  }, []);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
          {TURKISH_MONTHS[month]} {year}
        </h2>
        <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
          {monthAppts.length} randevu
        </span>
      </div>

      {/* Drag hint */}
      {draggingId && (
        <div className="px-3 py-1.5 text-[10px] font-medium text-center rounded-spa-sm" style={{ background: 'rgba(201,169,98,0.1)', color: 'var(--gold)' }}>
          <i className="ri-drag-move-line mr-1" /> Randevuyu istediğiniz güne bırakın
        </div>
      )}

      <div className="spa-card overflow-hidden">
        {/* Day labels */}
        <div className="grid grid-cols-7 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {DAY_LABELS.map(label => (
            <div key={label} className="p-2 text-center">
              <span className="text-[10px] font-semibold" style={{ color: 'var(--text-muted)' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {calendarData.map(({ date, dayNum, isCurrentMonth }) => {
            const dayAppts = apptsByDate[date] || [];
            const isToday = date === todayStr;
            const isExpanded = expandedDay === date;
            const isDropTarget = dropTargetDate === date;

            return (
              <div
                key={date}
                className="border-b border-r min-h-[110px] p-1.5 flex flex-col gap-1 transition-colors"
                style={{
                  borderColor: 'var(--border-color)',
                  background: isDropTarget
                    ? 'rgba(16,185,129,0.15)'
                    : isToday
                      ? 'rgba(201,169,98,0.08)'
                      : undefined,
                  opacity: isCurrentMonth ? 1 : 0.5,
                  cursor: draggingId ? 'copy' : 'pointer',
                }}
                onClick={() => {
                  if (draggingId) return;
                  if (dayAppts.length === 0) {
                    onDayClick(date);
                  } else {
                    setExpandedDay(isExpanded ? null : date);
                  }
                }}
                onDragOver={(e) => draggingId && handleDragOver(e, date)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, date)}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-xs font-semibold w-6 h-6 rounded-full flex items-center justify-center"
                    style={{
                      color: isToday ? '#FFFFFF' : isCurrentMonth ? 'var(--text-primary)' : 'var(--text-muted)',
                      background: isToday ? 'var(--gold)' : undefined,
                    }}
                  >
                    {dayNum}
                  </span>
                  {dayAppts.length > 0 && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                      {dayAppts.length}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1 mt-1">
                  {(isExpanded ? dayAppts : dayAppts.slice(0, 3)).map(appt => {
                    const isDragging = draggingId === appt.id;
                    return (
                      <button
                        key={appt.id}
                        draggable={!!onAppointmentUpdate}
                        onDragStart={(e) => handleDragStart(e, appt)}
                        onDragEnd={handleDragEnd}
                        onClick={(e) => { e.stopPropagation(); if (!draggingId) onAppointmentClick(appt); }}
                        className="w-full text-left px-1.5 py-1 rounded-md border-l-2 transition-all hover:brightness-95 cursor-grab active:cursor-grabbing select-none"
                        style={{
                          background: getStatusColor(appt.status) + '12',
                          borderLeftColor: getStatusColor(appt.status),
                          opacity: isDragging ? 0.6 : 1,
                        }}
                      >
                        <p className="text-[10px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                          {appt.time} {appt.customerName}
                        </p>
                        <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>
                          {appt.service}
                        </p>
                        {appt.packageName && (
                          <p className="text-[8px] font-semibold truncate mt-0.5" style={{ color: '#C9A962' }}>
                            <i className="ri-gift-line mr-0.5" />{appt.packageName}
                          </p>
                        )}
                      </button>
                    );
                  })}
                  {!isExpanded && dayAppts.length > 3 && (
                    <span className="text-[9px] text-center py-0.5" style={{ color: 'var(--gold)' }}>
                      +{dayAppts.length - 3} daha
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}